import { useState, useEffect } from "react";
import { Can } from "@/components/Can";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Search, X, Plus } from "lucide-react";
import { matchesQuery, findBySku, buildImagePrompt } from "./buttonConfigUtils";
import { AIButtonImageDialog } from "./AIButtonImageDialog";

/* Full configure dialog for a single sub-button slot */
export function SubSlotDialog({ slot, current, allProducts, employee, onSave, onClear, onClose }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [showAIDialog, setShowAIDialog] = useState(false);

  const canGenerateAI = ["owner", "admin", "manager"].includes(employee?.role);

  useEffect(() => {
    if (current) {
      const product = current.product_id ? allProducts.find(p => p.id === current.product_id) : null;
      setSelectedProduct(product || null);
      setDisplayName(current.label || product?.product_name || product?.name || "");
      setImageUrl(current.image_url || "");
    } else {
      setSelectedProduct(null);
      setDisplayName("");
      setImageUrl("");
    }
    setSearchQuery("");
  }, [slot, current, allProducts]);

  const filteredProducts = searchQuery.trim().length >= 3
    ? allProducts.filter(p => matchesQuery(p, searchQuery.trim())).slice(0, 20)
    : [];

  const pickProduct = (product) => {
    setSelectedProduct(product);
    if (!displayName) setDisplayName(product.product_name || product.name);
    setSearchQuery("");
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const exact = findBySku(allProducts, searchQuery);
    if (exact) { pickProduct(exact); toast.success(`Linked ${exact.product_name || exact.name}`); }
    else if (filteredProducts.length === 1) pickProduct(filteredProducts[0]);
    else if (searchQuery.trim()) toast.error("No product with that SKU");
  };

  const canAssign = !!displayName.trim() && !!selectedProduct;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh]" data-testid="sub-slot-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">
            Configure Sub-Button (Slot {slot + 1})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Name */}
          <div>
            <Label className="text-sm font-bold">Button Name *</Label>
            <Input
              value={displayName}
              data-testid="sub-slot-display-name"
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter button name..."
              className="mt-1 h-10 text-base"
            />
          </div>

          {/* Image URL */}
          <div>
            <Label className="text-sm">Image Location (optional)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Image URL or file path..."
                className="h-9 flex-1"
                data-testid="sub-slot-image-url"
              />
              {canGenerateAI && (
                <Can k="products.button_config.ai_image"><Button variant="outline" size="sm" className="h-9 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap" data-testid="sub-slot-generate-ai" onClick={() => setShowAIDialog(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> +Image
                </Button></Can>
              )}
            </div>
            {imageUrl && (
              <div className="mt-1 flex items-center gap-2">
                <img src={imageUrl} className="w-10 h-10 object-contain rounded border" alt="" onError={(e) => e.target.style.display='none'} />
                <span className="text-[10px] text-zinc-400">Preview</span>
              </div>
            )}
          </div>

          {/* Required: Link to product */}
          <div>
            <Label className="text-sm font-bold">Link to Product * <span className="text-zinc-400 font-normal">(search by name or scan SKU + Enter)</span></Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
              <Input
                data-testid="sub-slot-product-search"
                placeholder="Search by name or scan SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10 h-9"
              />
            </div>
            {searchQuery.trim().length >= 3 && (
              <ScrollArea className="h-36 border rounded-lg mt-1">
                {filteredProducts.length === 0 ? (
                  <div className="text-center text-zinc-400 text-sm py-6">No products found</div>
                ) : (
                  filteredProducts.map(product => (
                    <div
                      key={product.id}
                      data-testid={`sub-slot-product-option-${product.id}`}
                      onClick={() => pickProduct(product)}
                      className={`flex items-center justify-between p-2 cursor-pointer border-b border-zinc-50 ${
                        selectedProduct?.id === product.id ? "bg-blue-50 border-l-2 border-l-blue-500" : "hover:bg-zinc-50"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">{product.product_name || product.name}</div>
                        <div className="text-[10px] text-zinc-500">SKU: {product.sku}</div>
                      </div>
                      <div className="text-sm font-bold font-mono text-emerald-600 shrink-0 ml-2">
                        ${(product.retail_price || product.price || 0).toFixed(2)}
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>
            )}
            {selectedProduct && (
              <div className="mt-1 p-2 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between" data-testid="sub-slot-selected-product">
                <div>
                  <div className="text-xs font-bold text-blue-800">{selectedProduct.product_name || selectedProduct.name}</div>
                  <div className="text-[10px] text-blue-600">SKU: {selectedProduct.sku}</div>
                </div>
                <button onClick={() => setSelectedProduct(null)} data-testid="sub-slot-remove-product" className="text-xs text-red-500 hover:text-red-700">Remove</button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => {
                if (!displayName.trim()) { toast.error("Button name is required"); return; }
                if (!selectedProduct) { toast.error("Link a product to this sub-button"); return; }
                onSave({
                  product_id: selectedProduct.id,
                  label: displayName.trim(),
                  image_url: imageUrl.trim() || null,
                });
              }}
              data-testid="sub-slot-assign"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold h-10"
              disabled={!canAssign}
            >
              Assign to Slot {slot + 1}
            </Button>
            {current && (
              <Button onClick={onClear} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-10">
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
            <Button onClick={onClose} variant="outline" className="h-10">Cancel</Button>
          </div>
        </div>
      </DialogContent>

      {showAIDialog && (
        <AIButtonImageDialog
          open={showAIDialog}
          initialPrompt={buildImagePrompt(displayName, selectedProduct?.product_name || selectedProduct?.name)}
          onClose={() => setShowAIDialog(false)}
          onApprove={(url) => { setImageUrl(url); setShowAIDialog(false); }}
        />
      )}
    </Dialog>
  );
}
